import { useState } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import { useTranslation } from '../i18n';

interface IExpandableMediaGridProps {
  mediaUrls: string[];
  onImageClick: (url: string) => void;
}

const MAX_VISIBLE = 4;

const ExpandableMediaGrid = ({
  mediaUrls,
  onImageClick,
}: IExpandableMediaGridProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const visibleUrls = expanded ? mediaUrls : mediaUrls.slice(0, MAX_VISIBLE);
  const hiddenCount = mediaUrls.length - MAX_VISIBLE;

  return (
    <div
      className={`mb-2 ${visibleUrls.length === 1 ? '' : 'grid grid-cols-2 gap-2'} max-w-lg`}
    >
      {visibleUrls.map((url, idx) => {
        // The last visible tile doubles as the "show the rest" control when
        // there are more images. Branching the tile's own handler keeps one
        // interactive element per tile — an overlay with its own onClick would
        // nest a second control inside this button.
        const isExpandTile =
          idx === MAX_VISIBLE - 1 && hiddenCount > 0 && !expanded;

        return (
          <button
            key={idx}
            type="button"
            onClick={() =>
              isExpandTile
                ? setExpanded(true)
                : onImageClick(getImageUrl(url) || url)
            }
            aria-label={
              isExpandTile
                ? t('common.show_more_media', { count: hiddenCount })
                : undefined
            }
            className="relative block cursor-pointer"
          >
            <img
              src={getImageUrl(url)}
              alt={`Media ${idx + 1}`}
              className="rounded-md w-full object-cover border border-gray-200 hover:opacity-90 transition-opacity"
            />
            {isExpandTile && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md text-white text-lg font-semibold hover:bg-black/60 transition-colors">
                +{hiddenCount}
              </span>
            )}
          </button>
        );
      })}
      {expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="col-span-2 text-xs text-gray-500 hover:text-gray-700 py-1"
        >
          {t('common.show_less')}
        </button>
      )}
    </div>
  );
};

export default ExpandableMediaGrid;
